import { useState } from 'react';

const GRAPH_PERMISSIONS_READ = [
  { name: 'User.Read.All', description: 'Read user profiles and status', product: 'Entra ID' },
  { name: 'Group.Read.All', description: 'Read group memberships', product: 'Entra ID' },
  { name: 'AuditLog.Read.All', description: 'Read sign-in and audit logs', product: 'Entra ID' },
  { name: 'IdentityRiskyUser.Read.All', description: 'Read risky user detections', product: 'Entra ID' },
  { name: 'Policy.Read.All', description: 'Read conditional access policies', product: 'Entra ID' },
  { name: 'MailboxSettings.Read', description: 'Read inbox rules and mailbox settings', product: 'Exchange' },
  { name: 'Application.Read.All', description: 'Read service principals and OAuth apps', product: 'Entra ID' },
  { name: 'SecurityAlert.Read.All', description: 'Read Defender alerts (all products)', product: 'Defender' },
  { name: 'SecurityIncident.Read.All', description: 'Read Defender incidents', product: 'Defender' },
  { name: 'ThreatHunting.Read.All', description: 'Run advanced hunting KQL queries', product: 'Defender' },
] as const;

const DEFENDER_PERMISSIONS = [
  { name: 'Machine.Read.All', description: 'Read machine details for actions' },
  { name: 'Machine.Isolate', description: 'Isolate/release machines from network' },
  { name: 'Machine.Scan', description: 'Initiate antivirus scans' },
] as const;

const GRAPH_PERMISSIONS_WRITE = [
  { name: 'User.ReadWrite.All', description: 'Disable/enable user accounts', product: 'Entra ID' },
  { name: 'UserAuthenticationMethod.ReadWrite.All', description: 'Reset MFA registration', product: 'Entra ID' },
  { name: 'MailboxSettings.ReadWrite', description: 'Disable/delete malicious inbox rules', product: 'Exchange' },
  { name: 'Application.ReadWrite.All', description: 'Disable OAuth apps and service principals', product: 'Entra ID' },
  { name: 'DelegatedPermissionGrant.ReadWrite.All', description: 'Revoke OAuth2 consent grants', product: 'Entra ID' },
  { name: 'SecurityAlert.ReadWrite.All', description: 'Update alert status and classification', product: 'Defender' },
  { name: 'SecurityIncident.ReadWrite.All', description: 'Update incident status and assignment', product: 'Defender' },
] as const;

const ALL_READ_PERMISSIONS = [
  ...GRAPH_PERMISSIONS_READ.map((p) => p.name),
  ...DEFENDER_PERMISSIONS.map((p) => p.name),
];

interface Props {
  onSubmit: (config: Record<string, unknown>) => void;
  isPending: boolean;
  error: string;
}

export function MicrosoftWizard({ onSubmit, isPending, error }: Props) {
  const [step, setStep] = useState(1);
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [secretExpiry, setSecretExpiry] = useState('24');
  const [includeWrite, setIncludeWrite] = useState(false);

  // Track which permissions were actually granted
  const [grantedPerms, setGrantedPerms] = useState<Set<string>>(new Set(ALL_READ_PERMISSIONS));
  const [grantedWritePerms, setGrantedWritePerms] = useState<Set<string>>(
    new Set(GRAPH_PERMISSIONS_WRITE.map((p) => p.name)),
  );

  function togglePerm(name: string) {
    setGrantedPerms((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleWritePerm(name: string) {
    setGrantedWritePerms((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const grantedReadCount = grantedPerms.size;
  const grantedWriteCount = includeWrite ? grantedWritePerms.size : 0;
  const hasEntraWrite = includeWrite && grantedWritePerms.has('User.ReadWrite.All');
  const hasDefenderWrite = includeWrite && (
    grantedWritePerms.has('SecurityAlert.ReadWrite.All') ||
    grantedWritePerms.has('SecurityIncident.ReadWrite.All')
  );

  function handleConnect() {
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + parseInt(secretExpiry, 10));

    onSubmit({
      tenantId,
      clientId,
      clientSecret,
      secretExpiresAt: expiresAt.toISOString(),
      entraWriteEnabled: hasEntraWrite,
      defenderWriteEnabled: hasDefenderWrite,
      sentinelEnabled: true,
      nationalCloud: 'global',
      grantedPermissions: [...grantedPerms, ...(includeWrite ? grantedWritePerms : [])],
    });
  }

  const canContinue: Record<number, boolean> = {
    1: tenantId.trim() !== '' && clientId.trim() !== '',
    2: clientSecret.trim() !== '',
    3: grantedReadCount > 0,
    4: true,
  };

  const stepLabels = ['Register App', 'Client Secret', 'Permissions', 'Connect'];

  return (
    <>
      {/* Step indicator */}
      <div className="wizard-steps">
        {stepLabels.map((label, i) => {
          const num = i + 1;
          const done = num < step;
          const active = num === step;
          return (
            <div key={num} style={{ display: 'flex', alignItems: 'center' }}>
              {i > 0 && <div className={`wizard-step-line${done ? ' done' : ''}`} />}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div className={`wizard-step-circle${done ? ' done' : ''}${active ? ' active' : ''}`}>
                  {done ? '✓' : num}
                </div>
                <span className="wizard-step-label">{label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="wizard-body">
        {step === 1 && (
          <>
            <div className="wizard-callout">
              <strong>Prerequisites:</strong> You need <strong>Global Administrator</strong> or{' '}
              <strong>Cloud Application Administrator</strong> role in Microsoft Entra ID.
            </div>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>Go to <strong>Azure Portal → Microsoft Entra ID → App registrations → New registration</strong></li>
              <li>Name the app <strong>"Cade Security Connector"</strong></li>
              <li>Set account type to <strong>"Accounts in this organizational directory only"</strong></li>
              <li>Leave Redirect URI <strong>blank</strong> and click <strong>Register</strong></li>
              <li>Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong> from the Overview page</li>
            </ol>
            <div className="form-field">
              <label htmlFor="wiz-tenant-id">Directory (Tenant) ID</label>
              <input
                id="wiz-tenant-id"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="b3c1b5fc-828c-45fa-..."
                autoComplete="off"
              />
            </div>
            <div className="form-field">
              <label htmlFor="wiz-client-id">Application (Client) ID</label>
              <input
                id="wiz-client-id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="e0da400f-affd-43ef-..."
                autoComplete="off"
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="wizard-callout warning">
              <strong>Important:</strong> The client secret value is shown only once when created. Copy it immediately.
            </div>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '12px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>In the app registration → <strong>Certificates & secrets</strong> → <strong>New client secret</strong></li>
              <li>Description: <strong>"Cade"</strong>, Expiry: <strong>24 months</strong> (recommended)</li>
              <li>Click <strong>Add</strong> and immediately copy the <strong>Value</strong> column</li>
            </ol>
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
              Copy the <strong>Value</strong>, not the Secret ID.
            </p>
            <div className="form-field">
              <label htmlFor="wiz-secret">Client Secret Value</label>
              <input
                id="wiz-secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Enter the secret value..."
                autoComplete="new-password"
              />
            </div>
            <div className="form-field">
              <label htmlFor="wiz-expiry">Secret Expiry</label>
              <select
                id="wiz-expiry"
                value={secretExpiry}
                onChange={(e) => setSecretExpiry(e.target.value)}
              >
                <option value="6">6 months</option>
                <option value="12">12 months</option>
                <option value="24">24 months (recommended)</option>
              </select>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <ol style={{ fontSize: 13, color: 'var(--color-text-secondary)', paddingLeft: 20, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li>In the app registration → <strong>API permissions</strong> → <strong>Add a permission</strong></li>
              <li>Select <strong>Microsoft Graph</strong> → <strong>Application permissions</strong> → add each permission below</li>
              <li>Click <strong>Add a permission</strong> again → <strong>APIs my organization uses</strong> → search <strong>"WindowsDefenderATP"</strong></li>
              <li>Select <strong>Application permissions</strong> and add the Defender permissions below</li>
              <li>Click <strong>"Grant admin consent for [your org]"</strong></li>
            </ol>

            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', margin: '8px 0 4px' }}>
              Microsoft Graph — Application permissions
            </p>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 4px' }}>
              Check each permission you were able to grant in Azure Portal
            </p>
            <div className="scope-list">
              {GRAPH_PERMISSIONS_READ.map((p) => (
                <label key={p.name} className="scope-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={grantedPerms.has(p.name)}
                    onChange={() => togglePerm(p.name)}
                    style={{ width: 'auto', marginRight: 4 }}
                  />
                  <span className="scope-item-name">{p.name}</span>
                  <span className="scope-item-desc">{p.description}</span>
                </label>
              ))}
            </div>

            <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', margin: '12px 0 4px' }}>
              WindowsDefenderATP — Application permissions
            </p>
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '0 0 4px' }}>
              Search "WindowsDefenderATP" under <em>APIs my organization uses</em>
            </p>
            <div className="scope-list">
              {DEFENDER_PERMISSIONS.map((p) => (
                <label key={p.name} className="scope-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={grantedPerms.has(p.name)}
                    onChange={() => togglePerm(p.name)}
                    style={{ width: 'auto', marginRight: 4 }}
                  />
                  <span className="scope-item-name">{p.name}</span>
                  <span className="scope-item-desc">{p.description}</span>
                </label>
              ))}
            </div>

            <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={includeWrite}
                  onChange={(e) => setIncludeWrite(e.target.checked)}
                />
                <span>Also grant write permissions <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>(optional — can be added later)</span></span>
              </label>
            </div>

            {includeWrite && (
              <>
                <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-primary)', margin: '8px 0 4px' }}>
                  Write permissions (Microsoft Graph)
                </p>
                <div className="scope-list">
                  {GRAPH_PERMISSIONS_WRITE.map((p) => (
                    <label key={p.name} className="scope-item" style={{ cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={grantedWritePerms.has(p.name)}
                        onChange={() => toggleWritePerm(p.name)}
                        style={{ width: 'auto', marginRight: 4 }}
                      />
                      <span className="scope-item-name">{p.name}</span>
                      <span className="scope-item-desc">{p.description}</span>
                    </label>
                  ))}
                </div>
              </>
            )}

            {grantedReadCount < ALL_READ_PERMISSIONS.length && (
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
                {grantedReadCount + grantedWriteCount} of {ALL_READ_PERMISSIONS.length + (includeWrite ? GRAPH_PERMISSIONS_WRITE.length : 0)} permissions granted — Cade will work with partial permissions but some tools may be unavailable
              </p>
            )}
            {grantedReadCount === ALL_READ_PERMISSIONS.length && (
              <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
                {grantedReadCount + grantedWriteCount} permissions total — don't forget to click "Grant admin consent"
              </p>
            )}
          </>
        )}

        {step === 4 && (
          <>
            <div className="wizard-summary">
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Tenant ID</span>
                <span>{tenantId.slice(0, 8)}{'•'.repeat(Math.max(0, tenantId.length - 8))}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Client ID</span>
                <span>{clientId.slice(0, 8)}{'•'.repeat(Math.max(0, clientId.length - 8))}</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Client Secret</span>
                <span style={{ color: 'var(--sev-success)' }}>✓ Provided</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Auth Method</span>
                <span>OAuth 2.0 client_credentials</span>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Products</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {grantedPerms.has('User.Read.All') && <span className="scope-pill">Entra ID</span>}
                  {(grantedPerms.has('SecurityAlert.Read.All') || grantedPerms.has('ThreatHunting.Read.All')) && <span className="scope-pill">Defender XDR</span>}
                  {grantedPerms.has('SecurityIncident.Read.All') && <span className="scope-pill">Sentinel</span>}
                  {grantedReadCount === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>No permissions selected</span>}
                </div>
              </div>
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Read permissions</span>
                <span>{grantedReadCount} of {ALL_READ_PERMISSIONS.length}</span>
              </div>
              {includeWrite && (
                <div className="wizard-summary-row">
                  <span className="wizard-summary-label">Write permissions</span>
                  <span>{grantedWriteCount} of {GRAPH_PERMISSIONS_WRITE.length}</span>
                </div>
              )}
              <div className="wizard-summary-row">
                <span className="wizard-summary-label">Secret expires</span>
                <span>In {secretExpiry} months</span>
              </div>
            </div>

            {grantedReadCount < ALL_READ_PERMISSIONS.length && (
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '8px 0 0' }}>
                Some permissions are missing — Cade will connect with reduced capabilities. You can add more permissions later in Azure Portal.
              </p>
            )}

            {error && <p className="login-error">{error}</p>}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="wizard-nav">
        <div>
          {step > 1 && (
            <button type="button" className="btn-secondary" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
        </div>
        <div>
          {step < 4 ? (
            <button
              type="button"
              className="btn-primary"
              disabled={!canContinue[step]}
              onClick={() => setStep(step + 1)}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={isPending}
              onClick={handleConnect}
            >
              {isPending ? 'Connecting...' : 'Connect'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
